import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import WalletStrip from "../components/WalletStrip";
import { Building2, Plus, Globe, Folder, Archive, ChevronRight, TrendingUp, X, BarChart3 } from "lucide-react";

const DOMAINS = ["Insurance", "Banking & Fintech", "Healthcare / BPO", "Telecom", "Retail / eCommerce", "Logistics", "Government Services", "Other / Generic", "Custom"];
const CURRENCIES = ["USD", "AED", "SAR", "OMR", "INR", "GBP", "EUR"];

const DEFAULT_FORM = { name: "", department: "", country: "", localization_target: 20, tolerance_band: 5, currency: "AED", domain: "Insurance", notes: "" };

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/companies"), api.get("/my/package")]);
      setCompanies(c.data);
      setPkg(p.data.package);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Company name is required");
    setCreating(true);
    setError("");
    try {
      await api.post("/companies", form);
      await loadAll();
      setShowCreate(false);
      setForm(DEFAULT_FORM);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Archive this company? (Companies cannot be deleted — the slot stays consumed.)")) return;
    try {
      await api.put(`/companies/${id}/archive`);
      await loadAll();
    } catch (err) {
      alert(formatError(err));
    }
  };

  const domainColors = {
    "Insurance": "bg-blue-50 text-blue-700 border-blue-200",
    "Banking & Fintech": "bg-green-50 text-green-700 border-green-200",
    "Healthcare / BPO": "bg-pink-50 text-pink-700 border-pink-200",
    "Telecom": "bg-orange-50 text-orange-700 border-orange-200",
    "Retail / eCommerce": "bg-purple-50 text-purple-700 border-purple-200",
    "Logistics": "bg-amber-50 text-amber-700 border-amber-200",
    "Government Services": "bg-teal-50 text-teal-700 border-teal-200",
    "Other / Generic": "bg-sage-50 text-sage-700 border-sage-200",
    "Custom": "bg-gray-50 text-gray-700 border-gray-200",
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Consultant Portfolio</p>
            <h1 className="font-heading text-3xl font-semibold text-sage-900">Company Workspaces</h1>
            <p className="text-sage-500 text-sm mt-1">Manage your client engagements and optimization projects</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="benchmark-companies-button"
              onClick={() => navigate("/benchmark")}
              disabled={companies.length < 2}
              className="flex items-center gap-2 border border-sage-300 text-sage-700 rounded-md px-3 py-2.5 text-sm font-semibold hover:bg-sage-50 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50"
              title={companies.length < 2 ? "Add at least 2 companies to benchmark" : "Compare companies side-by-side"}
            >
              <BarChart3 className="w-4 h-4" />
              Benchmark
            </button>
            <button
              data-testid="create-company-button"
              onClick={() => setShowCreate(true)}
              disabled={pkg && pkg.slots_remaining === 0}
              className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-sage-700 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:opacity-50"
              title={pkg && pkg.slots_remaining === 0 ? "No slots remaining — buy a top-up" : ""}
            >
              <Plus className="w-4 h-4" />
              New Company
            </button>
          </div>
        </div>

        <WalletStrip pkg={pkg} />

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total Companies", value: companies.length, icon: Building2 },
            { label: "Total Projects", value: companies.reduce((a, c) => a + (c.project_count || 0), 0), icon: Folder },
            { label: "Active Domains", value: new Set(companies.map((c) => c.domain)).size, icon: Globe },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white border border-[#E2E8E3] rounded-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-sage-500 font-semibold">{label}</p>
                  <p className="font-heading text-3xl font-semibold text-sage-900 mt-1">{value}</p>
                </div>
                <div className="w-10 h-10 bg-sage-50 rounded-md flex items-center justify-center">
                  <Icon className="w-5 h-5 text-sage-500" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Company cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-sage-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : companies.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20 border border-dashed border-sage-200 rounded-md bg-white">
            <Building2 className="w-12 h-12 text-sage-300 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-medium text-sage-700 mb-1">No companies yet</h3>
            <p className="text-sage-400 text-sm mb-6">Create your first company workspace to start modelling</p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 transition-colors"
            >
              Create Company
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <div
                key={company.id}
                data-testid={`company-card-${company.id}`}
                onClick={() => navigate(`/workspace/${company.id}`)}
                className="bg-white border border-[#E2E8E3] rounded-md p-5 cursor-pointer hover:border-sage-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-sage-100 rounded-md flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-sage-600" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${domainColors[company.domain] || domainColors["Other / Generic"]}`}>
                      {company.domain}
                    </span>
                    <button
                      data-testid={`archive-company-${company.id}`}
                      onClick={(e) => handleArchive(e, company.id)}
                      className="opacity-0 group-hover:opacity-100 text-amber-500 hover:text-amber-700 transition-all"
                      title="Archive (slot stays consumed)"
                    >
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <h3 className="font-heading text-base font-semibold text-sage-900 mb-0.5">{company.name}</h3>
                {company.department && <p className="text-sage-500 text-xs mb-3">{company.department}</p>}
                <div className="flex items-center justify-between text-xs text-sage-400 mt-3 pt-3 border-t border-sage-50">
                  <div className="flex items-center gap-1">
                    <Folder className="w-3.5 h-3.5" />
                    <span>{company.project_count} project{company.project_count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5" />
                    <span>{company.country || "—"} · {company.currency}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-sage-400">
                    Local target: <span className="font-semibold text-sage-600">{company.localization_target}%</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-sage-300 group-hover:text-sage-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Company Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div data-testid="create-company-modal" className="bg-white rounded-md border border-sage-200 w-full max-w-lg animate-fade-in shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-sage-100">
              <h3 className="font-heading text-lg font-semibold text-sage-900">New Company Workspace</h3>
              <button onClick={() => setShowCreate(false)} className="text-sage-400 hover:text-sage-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Company Name *</label>
                  <input data-testid="company-name-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Gulf Insurance Co." className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Department / BU</label>
                  <input type="text" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Claims Operations" className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Country / Region</label>
                  <input type="text" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. UAE" className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Business Domain</label>
                  <select data-testid="company-domain-select" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none bg-white">
                    {DOMAINS.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none bg-white">
                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Local Target %</label>
                  <input type="number" min="0" max="100" value={form.localization_target} onChange={(e) => setForm({ ...form, localization_target: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Tolerance Band ±pp</label>
                  <input type="number" min="0" max="20" value={form.tolerance_band} onChange={(e) => setForm({ ...form, tolerance_band: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
                </div>
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-sage-200 text-sage-700 rounded-md py-2 text-sm font-semibold hover:bg-sage-50 transition-colors">Cancel</button>
                <button data-testid="confirm-create-company" type="submit" disabled={creating} className="flex-1 bg-sage-600 text-white rounded-md py-2 text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-60">
                  {creating ? "Creating..." : "Create Workspace"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
